import React, { useState, useMemo } from 'react';
import { colorVisitaPorArea, AREA_VISITAS_COLORES } from '../constants/config';

const DIAS_SEMANA = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const parseLocalDate = (fecha) => {
    if (!fecha) return null;
    const [y, m, d] = String(fecha).split('T')[0].split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
};

const mismaFecha = (a, b) =>
    a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const CalendarioVisitas = ({ visitas = [], obras = [], filtroObraFijo = '', filtroAreaResponsable = '', onDobleClickFecha, onDobleClickVisita }) => {
    const [vista, setVista] = useState('mes');
    const [mesActual, setMesActual] = useState(() => new Date());
    const [semanaInicio, setSemanaInicio] = useState(() => {
        const hoy = new Date();
        const dia = (hoy.getDay() + 6) % 7; // lunes=0
        const d = new Date(hoy);
        d.setDate(hoy.getDate() - dia);
        d.setHours(0, 0, 0, 0);
        return d;
    });
    const [filtroObra, setFiltroObra] = useState(filtroObraFijo ? String(filtroObraFijo) : '');
    const [busquedaFiltro, setBusquedaFiltro] = useState('');
    const [filtroArea, setFiltroArea] = useState('');
    const [filtroPersonal, setFiltroPersonal] = useState('');
    const [filtroPartido, setFiltroPartido] = useState('');
    const [filtroMotivo, setFiltroMotivo] = useState('');

    // Si ingresamos por una obra con filtro de área, mostramos todas las obras de esa área.
    const filtrarPorArea = Boolean(filtroAreaResponsable);

    const visitasFiltradas = visitas.filter(v => {
        // Filtro por área (modo "ingresé por una obra del área")
        if (filtrarPorArea) {
            const areaVisita = v.area_responsable;
            if (!areaVisita || String(areaVisita) !== String(filtroAreaResponsable)) return false;
        }
        // Filtro por área (select del calendario general)
        if (filtroArea) {
            const areaVisita = v.area_responsable;
            if (!areaVisita || String(areaVisita) !== String(filtroArea)) return false;
        }
        // Filtro por obra puntual (modo por defecto / buscador general)
        const fijo = filtroObraFijo ? String(filtroObraFijo) : '';
        const activo = fijo || filtroObra;
        if (activo && String(v.id_tabla_obras) !== String(activo)) return false;
        // Filtro por personal asignado
        if (filtroPersonal) {
            const nombres = (v.personal || []).map(p => p?.name);
            if (!nombres.includes(filtroPersonal)) return false;
        }
        // Filtro por partido
        if (filtroPartido && String(v.partido_nombre || '') !== filtroPartido) return false;
        // Filtro por motivo
        if (filtroMotivo) {
            const motivos = Array.isArray(v.motivo) ? v.motivo : (v.motivo ? [v.motivo] : []);
            if (!motivos.includes(filtroMotivo)) return false;
        }
        return true;
    });

    // Opciones para los filtros comunes (personal / partido / motivo)
    const personalUnicos = useMemo(() =>
        [...new Set(visitas.flatMap(v => (v.personal || []).map(p => p?.name).filter(Boolean)))].sort(),
    [visitas]);

    const partidosUnicos = useMemo(() =>
        [...new Set(visitas.map(v => v.partido_nombre).filter(Boolean))].sort(),
    [visitas]);

    const motivosUnicos = useMemo(() => {
        const set = new Set();
        visitas.forEach(v => {
            const ms = Array.isArray(v.motivo) ? v.motivo : (v.motivo ? [v.motivo] : []);
            ms.forEach(m => { if (m) set.add(m); });
        });
        return [...set].sort();
    }, [visitas]);

    const visitasDelDia = (dia) => {
        const d = parseLocalDate(dia);
        if (!d) return [];
        return visitasFiltradas
            .filter(v => {
                const vd = parseLocalDate(v.fecha);
                const vh = parseLocalDate(v.fecha_hasta);
                if (vh) return d >= vd && d <= vh;
                return mismaFecha(vd, d);
            })
            .map(v => ({ visita: v, esPrimerDia: mismaFecha(parseLocalDate(v.fecha), d) }));
    };

    // Obras con visitas para el filtro, con búsqueda por id_partido_obra o nombre
    const obrasConVisitas = useMemo(() => {
        const mapa = new Map();
        visitas.forEach(v => {
            if (!v.id_tabla_obras) return;
            const key = String(v.id_tabla_obras);
            if (!mapa.has(key)) {
                const base = obras.find(o => String(o.id) === key);
                mapa.set(key, {
                    id: v.id_tabla_obras,
                    label: base?.label || `${v.id_partido_obra || ''} — ${v.nombre_obra || ''}`.trim() || `Obra ${v.id_tabla_obras}`,
                });
            }
        });
        return Array.from(mapa.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [visitas, obras]);

    // Resultados de la búsqueda escribible (solo obras con visitas)
    const resultadosFiltro = busquedaFiltro.trim()
        ? obrasConVisitas.filter(o =>
            o.label.toLowerCase().includes(busquedaFiltro.trim().toLowerCase())
        ).slice(0, 10)
        : [];

    const cambiarMes = (delta) => {
        const nuevo = new Date(mesActual);
        nuevo.setMonth(nuevo.getMonth() + delta);
        setMesActual(nuevo);
    };

    const cambiarSemana = (delta) => {
        const nuevo = new Date(semanaInicio);
        nuevo.setDate(nuevo.getDate() + delta * 7);
        setSemanaInicio(nuevo);
    };

    const irHoy = () => {
        const hoy = new Date();
        setMesActual(new Date());
        const dia = (hoy.getDay() + 6) % 7;
        const d = new Date(hoy);
        d.setDate(hoy.getDate() - dia);
        d.setHours(0, 0, 0, 0);
        setSemanaInicio(d);
    };

    // Construcción de grilla del mes
    const inicioMes = new Date(mesActual.getFullYear(), mesActual.getMonth(), 1);
    const diasEnMes = new Date(mesActual.getFullYear(), mesActual.getMonth() + 1, 0).getDate();
    const offset = (inicioMes.getDay() + 6) % 7; // lunes=0
    const celdasMes = [];
    for (let i = 0; i < offset; i++) celdasMes.push(null);
    for (let d = 1; d <= diasEnMes; d++) celdasMes.push(new Date(mesActual.getFullYear(), mesActual.getMonth(), d));

    // Construcción de grilla de la semana
    const celdasSemana = [];
    for (let i = 0; i < 7; i++) {
        const d = new Date(semanaInicio);
        d.setDate(semanaInicio.getDate() + i);
        celdasSemana.push(d);
    }

    const hoy = new Date();
    const esHoy = (d) => mismaFecha(d, hoy);

    const renderCelda = (dia, esDomingo = false) => {
        const visitasDia = dia ? visitasDelDia(dia.toISOString().split('T')[0]) : [];
        const celdaHoy = dia && esHoy(dia);
        return (
            <div
                key={dia ? dia.toISOString() : Math.random()}
                onDoubleClick={() => dia && onDobleClickFecha && onDobleClickFecha(dia)}
                className={`min-h-[110px] p-1.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors ${
                    esDomingo ? 'bg-gray-50 dark:bg-gray-900' : ''
                } ${celdaHoy ? 'ring-2 ring-blue-500 ring-inset bg-blue-50 dark:bg-blue-950/40' : ''}`}
            >
                {dia && (
                    <>
                        <div className={`text-xs font-medium mb-1 ${celdaHoy ? 'text-blue-700 dark:text-blue-300 font-bold' : 'text-gray-500 dark:text-gray-400'}`}>
                            {dia.getDate()}
                        </div>
                        <div className="space-y-1 overflow-y-auto max-h-[78px] pr-0.5">
                            {visitasDia.map(({ visita: v, esPrimerDia }) => {
                                const color = colorVisitaPorArea(v.area_responsable);
                                const etiquetaObra = v.id_partido_obra
                                    ? `${v.id_partido_obra}${v.nombre_obra ? ` — ${v.nombre_obra}` : ''}`
                                    : color.label;
                                return (
                                    <div
                                        key={v.id}
                                        title={`${etiquetaObra} — ${Array.isArray(v.motivo) ? v.motivo.join(' / ') : (v.motivo || '')}${v.observaciones ? ` — ${v.observaciones}` : ''} (doble clic para editar)`}
                                        onDoubleClick={(e) => { e.stopPropagation(); onDobleClickVisita && onDobleClickVisita(v); }}
                                        style={{ backgroundColor: color.hex, borderColor: color.border }}
                                        className="px-3 py-2 rounded-md border-2 text-xs leading-snug text-gray-800 truncate cursor-pointer hover:brightness-95"
                                    >
                                        {v.hora && esPrimerDia ? <span className="font-bold">{String(v.hora).slice(0, 5)}</span> : null}
                                        {v.hora && esPrimerDia ? <span> · </span> : null}
                                        <span className="font-bold">{v.id_partido_obra || etiquetaObra}</span>
                                        {v.id_partido_obra && v.nombre_obra ? <span> · {v.nombre_obra}</span> : null}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="w-full">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div className="flex flex-wrap items-center gap-3">
                {!filtroObraFijo && !filtrarPorArea && (
                    <>
                        <div className="relative">
                            <input
                                value={busquedaFiltro}
                                onChange={(e) => {
                                    setBusquedaFiltro(e.target.value);
                                    if (!e.target.value) setFiltroObra('');
                                }}
                                placeholder="Buscar obra con visitas (id o nombre)..."
                                className={`px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 dark:bg-gray-800 dark:text-white dark:border-gray-700 h-10 min-w-[280px] ${filtroObra ? 'pr-8' : ''}`}
                            />
                            {busquedaFiltro.trim() && !filtroObra && (
                                <div className="absolute z-20 mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-md max-h-48 overflow-y-auto bg-white dark:bg-gray-800 shadow-lg">
                                    {resultadosFiltro.length === 0 ? (
                                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">Sin resultados</div>
                                    ) : (
                                        resultadosFiltro.map(o => (
                                            <button
                                                key={o.id}
                                                type="button"
                                                onClick={() => {
                                                    setFiltroObra(String(o.id));
                                                    setBusquedaFiltro(o.label);
                                                }}
                                                className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors"
                                            >
                                                {o.label}
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}
                            {filtroObra && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setFiltroObra('');
                                        setBusquedaFiltro('');
                                    }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                        <select
                            value={filtroArea}
                            onChange={(e) => setFiltroArea(e.target.value)}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 dark:bg-gray-800 dark:text-white dark:border-gray-700 h-10"
                        >
                            <option value="">Todas las áreas</option>
                            {Object.entries(AREA_VISITAS_COLORES).map(([valor, a]) => (
                                <option key={valor} value={valor}>{a.label}</option>
                            ))}
                        </select>
                    </>
                )}
                {filtrarPorArea && (
                    <div
                        className="px-3 py-2 text-sm font-medium rounded-md border"
                        style={{
                            backgroundColor: colorVisitaPorArea(filtroAreaResponsable).hex,
                            borderColor: colorVisitaPorArea(filtroAreaResponsable).border,
                            color: '#1f2937',
                        }}
                    >
                        Mostrando visitas del área: {colorVisitaPorArea(filtroAreaResponsable).label}
                    </div>
                )}
                <select
                    value={filtroPersonal}
                    onChange={(e) => setFiltroPersonal(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 dark:bg-gray-800 dark:text-white dark:border-gray-700 h-10"
                >
                    <option value="">Todas las personas</option>
                    {personalUnicos.map(p => (
                        <option key={p} value={p}>{p}</option>
                    ))}
                </select>
                <select
                    value={filtroPartido}
                    onChange={(e) => setFiltroPartido(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 dark:bg-gray-800 dark:text-white dark:border-gray-700 h-10"
                >
                    <option value="">Todos los partidos</option>
                    {partidosUnicos.map(p => (
                        <option key={p} value={p}>{p}</option>
                    ))}
                </select>
                <select
                    value={filtroMotivo}
                    onChange={(e) => setFiltroMotivo(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 dark:bg-gray-800 dark:text-white dark:border-gray-700 h-10"
                >
                    <option value="">Todos los motivos</option>
                    {motivosUnicos.map(m => (
                        <option key={m} value={m}>{m}</option>
                    ))}
                </select>
                {(filtroArea || filtroObra || filtroPersonal || filtroPartido || filtroMotivo) && (
                    <button
                        type="button"
                        onClick={() => {
                            setFiltroArea('');
                            setFiltroObra('');
                            setBusquedaFiltro('');
                            setFiltroPersonal('');
                            setFiltroPartido('');
                            setFiltroMotivo('');
                        }}
                        className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white rounded-md transition-colors h-10"
                    >
                        Limpiar filtros
                    </button>
                )}
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex rounded-md shadow-sm">
                        <button
                            onClick={() => setVista('mes')}
                            className={`px-3 py-2 text-sm border rounded-l-md ${vista === 'mes' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 dark:bg-gray-700 dark:text-gray-200'} border-gray-300 dark:border-gray-600`}
                        >
                            Mes
                        </button>
                        <button
                            onClick={() => setVista('semana')}
                            className={`px-3 py-2 text-sm border rounded-r-md ${vista === 'semana' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 dark:bg-gray-700 dark:text-gray-200'} border-gray-300 dark:border-gray-600`}
                        >
                            Semana
                        </button>
                    </div>
                    <button
                        onClick={irHoy}
                        className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white rounded-md transition-colors"
                    >
                        Hoy
                    </button>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => (vista === 'mes' ? cambiarMes(-1) : cambiarSemana(-1))}
                            className="px-2 py-2 bg-white hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md"
                        >
                            &laquo;
                        </button>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[150px] text-center">
                            {vista === 'mes'
                                ? `${MESES[mesActual.getMonth()]} ${mesActual.getFullYear()}`
                                : `Semana del ${semanaInicio.getDate()}/${semanaInicio.getMonth() + 1}/${semanaInicio.getFullYear()}`}
                        </span>
                        <button
                            onClick={() => (vista === 'mes' ? cambiarMes(1) : cambiarSemana(1))}
                            className="px-2 py-2 bg-white hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md"
                        >
                            &raquo;
                        </button>
                    </div>
                </div>
            </div>

            <div className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                Doble clic sobre una fecha para agendar una visita. Doble clic sobre una visita para editarla o eliminarla.
            </div>

            {vista === 'mes' ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700">
                        {DIAS_SEMANA.map(d => (
                            <div key={d} className="px-2 py-2 text-center text-xs font-semibold text-gray-500 dark:text-gray-400">{d}</div>
                        ))}
                    </div>
                    <div className="grid grid-cols-7">
                        {celdasMes.map((dia, idx) => renderCelda(dia, (idx + 1) % 7 === 0))}
                    </div>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700">
                        {celdasSemana.map(d => (
                            <div key={d.toISOString()} className="px-2 py-2 text-center text-xs font-semibold text-gray-500 dark:text-gray-400">
                                {DIAS_SEMANA[(d.getDay() + 6) % 7]} {d.getDate()}
                            </div>
                        ))}
                    </div>
                    <div className="grid grid-cols-7">
                        {celdasSemana.map((dia, idx) => renderCelda(dia, (idx + 1) % 7 === 0))}
                    </div>
                </div>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-600 dark:text-gray-300">
                <span className="font-medium text-gray-500 dark:text-gray-400">Áreas:</span>
                {Object.values(AREA_VISITAS_COLORES)
                    .filter((a, i, arr) => arr.findIndex(x => x.label === a.label) === i)
                    .map(col => (
                        <span key={col.label} className="flex items-center gap-1.5">
                            <span
                                className="inline-block w-4 h-4 rounded border-2"
                                style={{ backgroundColor: col.hex, borderColor: col.border }}
                            ></span>
                            {col.label}
                        </span>
                    ))}
            </div>
        </div>
    );
};

export default CalendarioVisitas;
